// FIXED useRealtime Hook
// Location: src/hooks/useRealtime.ts

import { useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface UseRealtimeOptions<T> {
  table: string;
  filter?: string;
  event?: 'INSERT' | 'UPDATE' | 'DELETE' | '*';
  onInsert?: (payload: T) => void;
  onUpdate?: (payload: T) => void;
  onDelete?: (payload: { old: T }) => void;
  enabled?: boolean;
}

export function useRealtime<T = any>({
  table,
  filter,
  event = '*',
  onInsert,
  onUpdate,
  onDelete,
  enabled = true
}: UseRealtimeOptions<T>) {
  const channelRef = useRef<RealtimeChannel | null>(null);
  
  // Store callbacks in ref to avoid recreating subscription
  const callbacksRef = useRef({ onInsert, onUpdate, onDelete });
  
  // Update callbacks ref when they change (but don't trigger effect)
  useEffect(() => {
    callbacksRef.current = { onInsert, onUpdate, onDelete };
  }, [onInsert, onUpdate, onDelete]);

  useEffect(() => {
    if (!enabled) return;

    const channelName = `realtime:${table}:${filter || 'all'}`;
    
    let channel = supabase.channel(channelName);

    const handlePayload = (payload: any) => {
      const { onInsert, onUpdate, onDelete } = callbacksRef.current;
      
      switch (payload.eventType) {
        case 'INSERT':
          onInsert?.(payload.new as T);
          break;
        case 'UPDATE':
          onUpdate?.(payload.new as T);
          break;
        case 'DELETE':
          onDelete?.({ old: payload.old as T });
          break;
      }
    };

    if (filter) {
      channel = channel.on(
        'postgres_changes',
        { 
          event, 
          schema: 'public', 
          table,
          filter 
        },
        handlePayload
      );
    } else {
      channel = channel.on(
        'postgres_changes',
        { 
          event, 
          schema: 'public', 
          table 
        },
        handlePayload
      );
    }

    channel.subscribe();
    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [table, filter, event, enabled]); // ✅ Fixed: Only table, filter, event, enabled

  return {
    channel: channelRef.current,
    isConnected: channelRef.current?.state === 'joined'
  };
}